/**
 * Words of the status indicator (the conflict banner's are in `query/labels.ts`) — 5 languages (storefront
 * en/ja/th/zh-Hant/zh-Hans; admin/POS ja/th/en). Plain words for non-technical staff
 * (design/04 §0): no "SSE", "stream", "version". Apps may pass their own `labels`.
 */
import type { ConnectionStatus } from "./types";

export type RealtimeLocale = "en" | "ja" | "th" | "zh-Hant" | "zh-Hans";

export interface RealtimeLabels {
  /** ● live */
  live: string;
  /** ○ connecting / reconnecting */
  reconnecting: string;
  /** ○ refreshes every 30 s (fallback poll) */
  fallback: string;
  /** ○ offline */
  offline: string;
  /** Tooltip of the indicator while live. */
  liveHint: string;
}

export const LABELS: Record<RealtimeLocale, RealtimeLabels> = {
  en: {
    live: "Live",
    reconnecting: "Reconnecting…",
    fallback: "Updates every 30 s",
    offline: "Offline",
    liveHint: "This screen updates by itself",
  },
  ja: {
    live: "ライブ",
    reconnecting: "再接続中…",
    fallback: "30秒ごとに更新",
    offline: "オフライン",
    liveHint: "この画面は自動で更新されます",
  },
  th: {
    live: "สด",
    reconnecting: "กำลังเชื่อมต่อใหม่…",
    fallback: "อัปเดตทุก 30 วิ",
    offline: "ออฟไลน์",
    liveHint: "หน้านี้อัปเดตเอง",
  },
  "zh-Hant": {
    live: "即時",
    reconnecting: "重新連線中…",
    fallback: "每 30 秒更新",
    offline: "離線",
    liveHint: "此頁面會自動更新",
  },
  "zh-Hans": {
    live: "实时",
    reconnecting: "正在重新连接…",
    fallback: "每 30 秒更新",
    offline: "离线",
    liveHint: "此页面会自动更新",
  },
};

/** `ja-JP` → `ja`, `zh-TW`/`zh-HK` → `zh-Hant`, `zh`/`zh-CN` → `zh-Hans`, anything else → `en`. */
export function realtimeLocale(locale: string | null | undefined): RealtimeLocale {
  const value = (locale ?? "").toLowerCase();
  if (value.startsWith("zh")) {
    return /hant|tw|hk|mo/.test(value) ? "zh-Hant" : "zh-Hans";
  }
  const base = value.split("-")[0];
  return base === "ja" || base === "th" ? base : "en";
}

export function labelsFor(
  locale: string | null | undefined,
  overrides?: Partial<RealtimeLabels>,
): RealtimeLabels {
  return { ...LABELS[realtimeLocale(locale)], ...overrides };
}

/**
 * Indicator state: `live` ●, `offline` ○, `fallback` ○ (polling), `reconnecting` ○; `null` when
 * nothing is subscribed (the indicator hides).
 */
export function indicatorOf(
  status: ConnectionStatus,
): "live" | "reconnecting" | "fallback" | "offline" | null {
  switch (status) {
    case "idle":
      return null;
    case "live":
    case "offline":
    case "fallback":
      return status;
    default:
      return "reconnecting"; // connecting / reconnecting / paused (hidden tab coming back)
  }
}
